import * as React from 'react'

import { Box, Flex } from '~ui/components/layout/Box'
import { H2 } from '~ui/components/typography/Heading/Heading'
import { TSingleSpace } from '~ui/styleGuide/spaces'
import { breakpoints } from '~ui/styleGuide/styles'

import { useLayoutCtx } from '../../config/contexts/LayoutProvider'
import { productDetailLayoutSettings } from '../../config/productDetailLayoutSettings'
import { useStickyStateCtx } from '../Sticky/StickyStateProvider'

type TProps = NoChildren & {
  /**
   * product name
   */
  nameLabel: string
  /**
   * horizontal padding of the content
   */
  horizontalPadding?: TSingleSpace
  /**
   * Should the component change its appearance in reaction on heading sticky position?
   * If sticky the heading will be truncated to 1 line and will get higher z-index.
   */
  hasStickyHeading?: boolean
}

const {
  mobileProductHeadingPaddingTop,
  mobileProductHeadingPaddingBottom,
  mobileProductHeadingFontSize,
} = productDetailLayoutSettings

const productStickyHeadingId = `productStickyHeadingId`
const OVERLAY_RENDER_DELAY = 500 // ms

/**
 * Encapsulates product heading and its sticky behavior.
 */
export const DetailInfoHeader: React.FC<TProps> = ({
  nameLabel,
  horizontalPadding = `none`,
  hasStickyHeading = false,
}) => {
  const { isStuck, setElementId } = useStickyStateCtx()
  const { isMobile, isTablet } = useLayoutCtx()

  const hasFullWidthHeader = isMobile || isTablet

  React.useEffect(() => {
    if (!hasStickyHeading) {
      return
    }

    // wait until the modal is open before connecting the observer to prevent it from firing unnecessarily
    const timeoutId = setTimeout(() => {
      setElementId(productStickyHeadingId)
    }, OVERLAY_RENDER_DELAY)

    return () => {
      setElementId(null)
      clearTimeout(timeoutId)
    }
  }, [hasStickyHeading, setElementId])

  const padding: PropsOf<typeof Flex>['padding'] = isMobile
    ? {
        top: mobileProductHeadingPaddingTop,
        bottom: mobileProductHeadingPaddingBottom,
        left: horizontalPadding,
        right: horizontalPadding,
      }
    : { vertical: `none`, horizontal: horizontalPadding }

  const refStickyBox = React.useRef<HTMLDivElement>(null)

  // FIX Chrome behavior "bug"
  // Chrome behaves differently when it comes to sticky content & content shrinking: https://stackoverflow.com/a/63460737
  // this causes content jumping which would re-trigger the IntersectionObserver bound to this header
  // as a workaround the sticky header’s height is set to the max height: https://stackoverflow.com/a/69855593
  // that also prevents content jumping
  // -> this effect should run when the content (`nameLabel`) changes – it’s intentional!
  React.useEffect(() => {
    if (!refStickyBox.current) {
      return
    }

    if (!hasStickyHeading) {
      refStickyBox.current.style.height = `unset`
      return
    }

    // on nameLabel change, the Box should not be stuck -> it should have it’s max height
    refStickyBox.current.style.height = `${refStickyBox.current.clientHeight}px`
  }, [nameLabel, hasStickyHeading])

  return (
    <Box
      // ! this Box should have mostly static props
      //  because the IntersectionObserver that is bound to it (via ID)
      //  seems to stop working when the element gets completely replaced by React
      //  (doesn’t seem to reconnect to the new DOM node & observes the removed one stored somewhere by reference)
      //  - these are all just wild guesses
      top={0}
      zIndex={2}
      ref={refStickyBox}
      id={productStickyHeadingId}
      position={isMobile && hasStickyHeading ? `sticky` : undefined}
    >
      <Flex
        gridColumn={hasFullWidthHeader ? `1/-1` : `auto`}
        justifyContent={`space-between`}
        alignItems={`center`}
        alignSelf={`start`}
        padding={padding}
        bgColor={hasStickyHeading && isStuck ? `bgColors.card` : undefined}
        shadow={hasStickyHeading && isStuck ? `bottomLong` : `none`}
      >
        <H2
          responsiveSize={
            isMobile
              ? undefined
              : {
                  maxBreakpoint: breakpoints.lg,
                  minBreakpoint: breakpoints.sm,
                  minSize: 24,
                  maxSize: 28,
                  unit: `px`,
                }
          }
          fontSize={isMobile ? mobileProductHeadingFontSize : undefined}
          fontStyle={`bold`}
          truncateOnLines={hasStickyHeading && isStuck ? 1 : undefined}
          wordBreak={`break-word`}
        >
          {nameLabel}
        </H2>
        {/* // TODO add once there is a place to store the information <ButtonLike liked={false} onClick={() => {}} />, as a prop -> React Node?  */}
      </Flex>
    </Box>
  )
}
